// hooks/useExcelDownload.js
import { useCallback } from "react";
import { showToast } from "services/utils/Status";

export const useExcelDownload = () => {
  const formatBooleanValue = (value) => {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes";
      if (value.toLowerCase() === "false") return "No";
    }

    return value;
  };

  const formatNumberFilterValue = (value, operator) => {
    const operatorSymbols = {
      equals: "=",
      notEquals: "≠",
      greaterEquals: "≥",
      lessEquals: "≤",
      between: "Between",
    };

    const symbol = operatorSymbols[operator] || "=";

    if (operator === "between" && value.fromNumber && value.toNumber) {
      return `${symbol} ${value.fromNumber} - ${value.toNumber}`;
    } else if (operator === "between" && value.fromNumber) {
      return `${symbol} From ${value.fromNumber}`;
    } else if (operator === "between" && value.toNumber) {
      return `${symbol} To ${value.toNumber}`;
    } else if (value.number !== undefined && value.number !== "") {
      return `${symbol} ${value.number}`;
    }

    return "";
  };

  const downloadExcel = useCallback(
    (data, headers, filename = "report", appliedFilters = {}) => {
      if (!data || data.length === 0) {
        showToast("error", "No data available to download");
        return;
      }

      try {
        const filteredHeaders = headers.filter((header) => {
          const headerName = (header.name || "").toLowerCase().trim();
          return (
            headerName !== "" &&
            headerName !== "Action" &&
            headerName !== "Actions"
          );
        });

        const headerNames = filteredHeaders.map((header) => header.name);
        const headerRow = headerNames.join(",");

        const dataRows = data.map((item) => {
          return filteredHeaders
            .map((header) => {
              let value;
              if (header.customFormatter) {
                value = header.customFormatter(item);
              } else {
                value = getNestedValue(item, header.key);
                if (value === null || value === undefined) {
                  value = "N/A";
                }
              }
              value = formatBooleanValue(value);
              return `"${value}"`;
            })
            .join(",");
        });

        // Build filter information section
        let filterInfo = [];
        if (appliedFilters && Object.keys(appliedFilters).length > 0) {
          Object.entries(appliedFilters).forEach(([field, value]) => {
            if (value) {
              const matchedHeader = headers.find(
                (header) => header.key === field
              );
              const displayName = matchedHeader ? matchedHeader.name : field;

              // Handle different filter types
              if (typeof value === "object") {
                // Date filter
                if (value.date !== undefined) {
                  if (value.operator === "equals" && value.date) {
                    filterInfo.push(`${displayName} : ${value.date}`);
                  } else if (value.operator === "greater" && value.date) {
                    filterInfo.push(`${displayName} : > ${value.date}`);
                  } else if (value.operator === "less" && value.date) {
                    filterInfo.push(`${displayName} : < ${value.date}`);
                  } else if (
                    value.operator === "between" &&
                    value.fromDate &&
                    value.toDate
                  ) {
                    filterInfo.push(
                      `${displayName} : ${value.fromDate} to ${value.toDate}`
                    );
                  } else if (value.operator === "between" && value.fromDate) {
                    filterInfo.push(`${displayName} : From ${value.fromDate}`);
                  } else if (value.operator === "between" && value.toDate) {
                    filterInfo.push(`${displayName} : To ${value.toDate}`);
                  }
                }
                // Time filter
                else if (value.time !== undefined) {
                  if (value.operator === "equals" && value.time) {
                    filterInfo.push(`${displayName} : ${value.time}`);
                  } else if (value.operator === "greater" && value.time) {
                    filterInfo.push(`${displayName} : > ${value.time}`);
                  } else if (value.operator === "less" && value.time) {
                    filterInfo.push(`${displayName} : < ${value.time}`);
                  } else if (
                    value.operator === "between" &&
                    value.fromTime &&
                    value.toTime
                  ) {
                    filterInfo.push(
                      `${displayName} : ${value.fromTime} to ${value.toTime}`
                    );
                  } else if (value.operator === "between" && value.fromTime) {
                    filterInfo.push(`${displayName} : From ${value.fromTime}`);
                  } else if (value.operator === "between" && value.toTime) {
                    filterInfo.push(`${displayName} : To ${value.toTime}`);
                  }
                }
                // Number filter (NEW)
                else if (
                  value.number !== undefined ||
                  value.fromNumber !== undefined ||
                  value.toNumber !== undefined
                ) {
                  const formattedValue = formatNumberFilterValue(
                    value,
                    value.operator
                  );
                  if (formattedValue) {
                    filterInfo.push(`${displayName} : ${formattedValue}`);
                  }
                }
                // Autocomplete filter (array)
                else if (Array.isArray(value)) {
                  if (
                    value.length > 0 &&
                    !(value.length === 1 && value[0] === "All")
                  ) {
                    filterInfo.push(`${displayName} : ${value.join(", ")}`);
                  }
                }
                // Boolean filter object
                else if (value.value !== undefined) {
                  filterInfo.push(
                    `${displayName} : ${value.value === "true" ? "Yes" : "No"}`
                  );
                }
              }
              // Handle simple values
              else {
                // Skip "All" for autocomplete
                if (value === "All" || value === "") {
                  return;
                }
                // Handle boolean strings
                if (value === "true" || value === "false") {
                  filterInfo.push(
                    `${displayName} : ${value === "true" ? "Yes" : "No"}`
                  );
                } else {
                  filterInfo.push(`${displayName} : ${value}`);
                }
              }
            }
          });

          // Add spacing between filter info and data
          if (filterInfo.length > 0) {
            filterInfo.push("");
            filterInfo.push("");
          }
        }

        const excelFix = "\uFEFF";
        const csvContent =
          excelFix +
          filterInfo.join("\n") +
          [headerRow, ...dataRows].join("\n");

        downloadFile(csvContent, filename);

        showToast("success", "Report downloaded successfully");
      } catch (error) {
        console.error("Download error:", error);
        showToast("error", "Download error: " + error.message);
      }
    },
    []
  );

  return { downloadExcel };
};

const getNestedValue = (obj, path) => {
  if (!path) return "";
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : "";
  }, obj);
};

const downloadFile = (content, filename) => {
  const blob = new Blob([content], { type: "text/csv; charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL object
  setTimeout(() => URL.revokeObjectURL(url), 100);
};
